import React, { useState, useEffect } from 'react';
import { 
  CurrencyDollarIcon, 
  BanknotesIcon, 
  CheckCircleIcon,
  ClockIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { staffApi } from '../staffApi';

export default function Payments() {
  const [tutorPayments, setTutorPayments] = useState([]);
  const [paymentRecords, setPaymentRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tutor-payments');
  const [viewingPayment, setViewingPayment] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const formatHours = (hours) => {
    const numericHours = Number(hours || 0);

    if (Number.isNaN(numericHours)) {
      return '0';
    }

    return numericHours.toFixed(2).replace(/\.?0+$/, '');
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tutorPaymentsData, paymentRecordsData] = await Promise.all([
        staffApi.getTutorPayments(),
        staffApi.getPaymentRecords()
      ]);
      
      // Ensure we have arrays
      const tutorPaymentsArray = Array.isArray(tutorPaymentsData?.data) ? tutorPaymentsData.data : [];
      const paymentRecordsArray = Array.isArray(paymentRecordsData?.data) ? paymentRecordsData.data : [];
      
      setTutorPayments(tutorPaymentsArray);
      setPaymentRecords(paymentRecordsArray);
    } catch (error) {
      console.error('Error fetching payment data:', error);
      // Set empty arrays on error to prevent map errors
      setTutorPayments([]);
      setPaymentRecords([]);
    } finally {
      setLoading(false);
    }
  };


  const handleMarkAsPaid = async (recordId) => {
    try {
      await staffApi.markPaymentAsPaid(recordId);
      await fetchData(); // Refresh data
    } catch (error) {
      console.error('Error marking payment as paid:', error);
    }
  };

  const handleViewPayment = (payment) => {
    setViewingPayment(payment);
    setShowViewModal(true);
  };

  const handleProcessPayout = async (tutorId) => {
    if (!window.confirm('Are you sure you want to process payout for this tutor? This will create a payment record and update all pending sessions.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await staffApi.processPayout(tutorId);
      
      if (response.success) {
        let successMessage = `Payout processed successfully!\nAmount: ${response.data.currency} ${response.data.total_amount}\nHours: ${formatHours(response.data.total_hours_processed)}\nSessions: ${response.data.sessions_processed}`;

        if (response.data.sessions_without_fee_count > 0) {
          successMessage += `\nWarning: ${response.data.sessions_without_fee_count} session(s) had no configured tutor fee and were recorded with zero payout amount.`;
        }

        alert(successMessage);
        await fetchData(); // Refresh data
      } else {
        alert(`Error: ${response.message}`);
      }
    } catch (error) {
      console.error('Error processing payout:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.errors?.detail ||
        'Failed to process payout. Please try again.';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodDisplay = (payment) => {
    if (payment.bank_account) return `Bank Account: ${payment.bank_account}`;
    if (payment.paypal) return `PayPal: ${payment.paypal}`;
    if (payment.upi) return `UPI: ${payment.upi}`;
    return 'Not specified';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon },
      'PAID': { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon }
    };
    
    const config = statusConfig[status] || statusConfig['PENDING'];
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
        <p className="text-gray-600">Manage tutor payment information and track payment records</p>
      </div>

      {/* Tabs */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('tutor-payments')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'tutor-payments'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Tutor Payment Info
            </button>
            <button
              onClick={() => setActiveTab('payment-records')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'payment-records'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Payment Records
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Tutor Payment Info Tab */}
          {activeTab === 'tutor-payments' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">Tutor Payment Information</h2>
                <p className="text-sm text-gray-500">{tutorPayments.length} tutors</p>
              </div>

              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tutor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment Method
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Notes
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Array.isArray(tutorPayments) ? tutorPayments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-indigo-600">
                                  {payment.tutor_name.split(' ').map(n => n[0]).join('')}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{payment.tutor_name}</div>
                              <div className="text-sm text-gray-500">{payment.tutor_email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <BanknotesIcon className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">{getPaymentMethodDisplay(payment)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {payment.notes || 'No notes'}
                            {Number(payment.pending_hours) > 0 && (
                              <div className="mt-1">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {formatHours(payment.pending_hours)} pending hours
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleViewPayment(payment)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="View Payment Details"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleProcessPayout(payment.tutor_id)}
                              disabled={Number(payment.pending_hours) <= 0}
                              className={`${
                                Number(payment.pending_hours) > 0 
                                  ? 'text-green-600 hover:text-green-900' 
                                  : 'text-gray-400 cursor-not-allowed'
                              }`}
                              title={
                                Number(payment.pending_hours) > 0 
                                  ? 'Process Payout' 
                                  : 'No pending hours to process'
                              }
                            >
                              <CurrencyDollarIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )) : null}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payment Records Tab */}
          {activeTab === 'payment-records' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">Payment Records</h2>
                <p className="text-sm text-gray-500">{paymentRecords.length} records</p>
              </div>

              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tutor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Package
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Array.isArray(paymentRecords) ? paymentRecords.map((record) => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{record.tutor_name}</div>
                          <div className="text-sm text-gray-500">{record.tutor_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{record.student_name}</div>
                          <div className="text-sm text-gray-500">{record.student_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{record.package_name}</div>
                          <div className="text-sm text-gray-500">{record.package_hours}h package</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <CurrencyDollarIcon className="h-4 w-4 text-gray-400 mr-1" />
                            <span className="text-sm font-medium text-gray-900">
                              {record.currency} {record.amount}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(record.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.payment_date}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {record.status === 'PENDING' && (
                            <button
                              onClick={() => handleMarkAsPaid(record.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Mark as Paid
                            </button>
                          )}
                        </td>
                      </tr>
                    )) : null}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* View Payment Modal */}
      {showViewModal && viewingPayment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Payment Details - {viewingPayment.tutor_name}
                </h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Tutor Information</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Name:</span>
                      <span className="ml-2 text-sm text-gray-900">{viewingPayment.tutor_name}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Email:</span>
                      <span className="ml-2 text-sm text-gray-900">{viewingPayment.tutor_email}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Payment Information</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Payment Type:</span>
                      <span className="ml-2 text-sm text-gray-900">{viewingPayment.payment_type || 'Not specified'}</span>
                    </div>
                    {viewingPayment.bank_account && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Account Holder:</span>
                        <span className="ml-2 text-sm text-gray-900">{viewingPayment.bank_account}</span>
                      </div>
                    )}
                    {viewingPayment.paypal && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">PayPal:</span>
                        <span className="ml-2 text-sm text-gray-900">{viewingPayment.paypal}</span>
                      </div>
                    )}
                    {viewingPayment.upi && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">UPI ID:</span>
                        <span className="ml-2 text-sm text-gray-900">{viewingPayment.upi}</span>
                      </div>
                    )}
                    {viewingPayment.wise && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Wise Membership:</span>
                        <span className="ml-2 text-sm text-gray-900">{viewingPayment.wise}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-sm font-medium text-gray-500">Notes:</span>
                      <span className="ml-2 text-sm text-gray-900">{viewingPayment.notes || 'No notes available'}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

